import React, {
  ComponentProps,
  ComponentType,
  lazy,
  LazyExoticComponent,
  ReactNode,
  Suspense,
} from "react";
import { reloadServerContext } from "@utils/context";
import { Route } from "react-router";
import goto from "./pageDefinitions/goto";
import { useOnce } from "./hooks/lifecycle";
import ScrollToTop from "./components/scroll-to-top/ScrollToTop";
import { matchPath } from "react-router-dom";
import { AppInitGate } from "./AppInitGate";

declare const PAGE_ROUTES: Record<
  /** routeToken */ string,
  /** modulePath */ string
>;

let isLoading = false;
export function isRouterLoading() {
  return isLoading;
}

function LoadingTracker({ children }: { children: ReactNode }) {
  useOnce(() => {
    isLoading = true;
    return () => {
      isLoading = false;
    };
  });

  return <>{children}</>;
}

/**
 * Get the lazy loaded component for the intended route. If it fails to do so,
 * reset to the baseline and track error.
 */
export function getLazyComponent<T extends ComponentType<any>>(
  dynamicImport: () => Promise<LazyExoticComponent<T>>,
  fallback: ReactNode = <div />
): T {
  const loadComponent = () =>
    dynamicImport().catch((err: Error) => {
      // If loading the baseline fails, then send the user to /error.
      goto.error(err, "lazy-load");
      return null;
    });
  const LazyLoadedComponent = lazy(loadComponent);

  // Ensure that we reload the server context on navigation
  return ((props: ComponentProps<T>) => {
    useOnce(() => {
      loadComponent();
      reloadServerContext();
    });

    return (
      <Suspense fallback={<LoadingTracker>{fallback}</LoadingTracker>}>
        <AppInitGate>
          <ScrollToTop />

          <LazyLoadedComponent {...props} />
        </AppInitGate>
      </Suspense>
    );
  }) as T;
}

export function getRouterComponents({
  routes = PAGE_ROUTES,
  fallback,
}: {
  routes?: Record<string, string>;
  fallback?: ReactNode;
} = {}) {
  return Object.keys(routes).map((path) => {
    const modulePath = routes[path];
    return (
      <Route
        key={path}
        exact
        path={`/${path}`}
        component={getLazyComponent(() => {
          // eslint-disable-next-line import/dynamic-import-chunkname
          return import(
            /* webpackExclude: /.*\.(stories|test)\..*$/ */ `./pages/${modulePath}`
          );
        }, fallback)}
      />
    );
  });
}

export function preloadPagePath(
  pathname: string,
  routes = PAGE_ROUTES
): Promise<void> {
  const matchingRoute = Object.keys(routes).find((key) =>
    matchPath(`/${pathname}`, { path: `/${key}` })
  );
  const matchingPath = routes[matchingRoute];
  if (matchingPath) {
    // eslint-disable-next-line import/dynamic-import-chunkname
    return import(`./pages/${matchingPath}`).catch(() => {
      // NOP: Will be reported if this is a user face error later
    });
  }
  return Promise.resolve();
}
